import React from 'react';
import { CloudSun, Map, Calendar, HelpCircle, Image, X } from 'lucide-react';

interface QuickActionsPanelProps {
  onActionClick: (action: string) => void;
  helpPromptsVisible?: boolean;
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean; // Optional prop to handle mobile styles
}

export const QuickActionsPanel: React.FC<QuickActionsPanelProps> = ({ 
  onActionClick, 
  helpPromptsVisible = false,
  isOpen,
  onClose,
  isMobile = false // Default to false if not provided
  }) => {
  
  // For mobile bottom sheet
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-[5400]"
            onClick={onClose}
          />
        )}
        
        {/* Bottom Sheet */}
        <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl z-[5700] transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}>
          <div className="p-4">
            {/* Drag handle */}
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            
            {/* Quick Actions */}
            <div className="space-y-3">
              <button onClick={() => { onActionClick('plan'); onClose(); }} 
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Create an Itinerary</div>
                  <div className="text-sm text-gray-500">Create a personalized itinerary for your journey</div>
                </div>
              </button>
              
              <button onClick={() => { onActionClick('weather'); onClose(); }}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <CloudSun className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Check Weather</div>
                  <div className="text-sm text-gray-500">Get current weather conditions for any location</div>
                </div>
              </button>
              
              <button onClick={() => { onActionClick('find'); onClose(); }}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Map className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Find Places</div>
                  <div className="text-sm text-gray-500">Discover attractions, restaurants, and more</div>
                </div>
              </button>
              
              <button onClick={() => { onActionClick('help'); onClose(); }}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Get Help</div>
                  <div className="text-sm text-gray-500">See example prompts and tips</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }
   
  if (!isOpen) return null;

  const handleAction = (action: string) => {
    console.log("action ",action)
    onActionClick(action);
    onClose();
  };

  return (
    <div 
      className="quick-actions-container absolute bottom-full left-4 mb-2 bg-white rounded-2xl shadow-lg border border-gray-200 p-1 z-50"
      style={{ width: 'var(--quick-actions-panel-width, calc(100% - 32px))' }}
    >
      {/* Close button */}
      {/* <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100"
        style={{ 
          width: 'calc(var(--quick-actions-close-icon-size) + 8px)',
          height: 'calc(var(--quick-actions-close-icon-size) + 8px)'
        }}
      >
        <X 
          size={16} 
          style={{ width: 'var(--quick-actions-close-icon-size)', height: 'var(--quick-actions-close-icon-size)' }}
          className="text-gray-500" 
        />
      </button> */}
      
      {/* Action items */}
      <div className="space-y-1">
        {/* <button
          onClick={() => handleAction('find')}
          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
        >
          <div 
            className="rounded-full bg-gray-100 flex items-center justify-center"
            style={{ 
              width: 'var(--quick-actions-icon-container-size)', 
              height: 'var(--quick-actions-icon-container-size)' 
            }}
          >
            <Image 
              size={20} 
              style={{ width: 'var(--quick-actions-icon-size)', height: 'var(--quick-actions-icon-size)' }}
              className="text-gray-700" 
            />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900" style={{ fontSize: 'var(--quick-actions-title-size)' }}>
              Upload a photo
            </p>
            <p className="text-gray-500" style={{ fontSize: 'var(--quick-actions-text-size)' }}>
              Start your journey with a travel-related photo
            </p>
          </div>
        </button> */}
        
        <button
          onClick={() => handleAction('plan')}
          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
        >
          <div 
            className="rounded-full bg-green-100 flex items-center justify-center"
            style={{ 
              width: 'var(--quick-actions-icon-container-size)', 
              height: 'var(--quick-actions-icon-container-size)' 
            }}
          >
            <Calendar 
              size={20} 
              style={{ width: 'var(--quick-actions-icon-size)', height: 'var(--quick-actions-icon-size)' }}
              className="text-green-600" 
            />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900" style={{ fontSize: 'var(--quick-actions-title-size)' }}>
              Create an Itinerary
            </p>
            <p className="text-gray-500" style={{ fontSize: 'var(--quick-actions-text-size)' }}>
              Create a personalized itinerary for your journey
            </p>
          </div>
        </button>
        
        <button
          onClick={() => handleAction('weather')}
          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
        >
          <div 
            className="rounded-full bg-blue-100 flex items-center justify-center"
            style={{ 
              width: 'var(--quick-actions-icon-container-size)', 
              height: 'var(--quick-actions-icon-container-size)' 
            }}
          >
            <CloudSun 
              size={20} 
              style={{ width: 'var(--quick-actions-icon-size)', height: 'var(--quick-actions-icon-size)' }}
              className="text-blue-600" 
            />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900" style={{ fontSize: 'var(--quick-actions-title-size)' }}>
              Check Weather
            </p>
            <p className="text-gray-500" style={{ fontSize: 'var(--quick-actions-text-size)' }}>
              Get current weather conditions for any location
            </p>
          </div>
        </button>
        
        <button
          onClick={() => handleAction('find')}
          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
        >
          <div 
            className="rounded-full bg-purple-100 flex items-center justify-center"
            style={{ 
              width: 'var(--quick-actions-icon-container-size)', 
              height: 'var(--quick-actions-icon-container-size)' 
            }}
          >
            <Map 
              size={20} 
              style={{ width: 'var(--quick-actions-icon-size)', height: 'var(--quick-actions-icon-size)' }}
              className="text-purple-600" 
            />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900" style={{ fontSize: 'var(--quick-actions-title-size)' }}>
              Find Places
            </p>
            <p className="text-gray-500" style={{ fontSize: 'var(--quick-actions-text-size)' }}>
              Discover attractions, restaurants, and more
            </p>
          </div>
        </button>
        
        <button
          onClick={() => handleAction('help')}
          disabled={helpPromptsVisible}
          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${
            helpPromptsVisible ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
        >
          <div 
            className="rounded-full bg-orange-100 flex items-center justify-center"
            style={{ 
              width: 'var(--quick-actions-icon-container-size)', 
              height: 'var(--quick-actions-icon-container-size)' 
            }}
          >
            <HelpCircle 
              size={20} 
              style={{ width: 'var(--quick-actions-icon-size)', height: 'var(--quick-actions-icon-size)' }}
              className={helpPromptsVisible ? 'text-gray-400' : 'text-orange-600'} 
            />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900" style={{ fontSize: 'var(--quick-actions-title-size)' }}>
              Get Help
            </p>
            <p className="text-gray-500" style={{ fontSize: 'var(--quick-actions-text-size)' }}>
              See example prompts and tips
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};
